/**
 * useMedicationLookups — fetch Swiss medication (SAI) info for one or more
 * drug names in parallel. One cached query per drug term, so re-typing or
 * re-ordering the search is cheap. Drives the compatibility app's per-drug
 * info cards.
 */

import { useQueries } from '@tanstack/react-query'
import { searchMedication, type MedicationSearchResponse } from '@/api/endpoints/medications'
import { QUERY_STALE_TIMES } from '@/lib/query-config'

/**
 * Minimum term length before we query. Mirrors the medication service's own
 * SearchRequest.term minimum (2 chars) — a backend contract value, not a
 * tunable, so it lives next to the call that enforces it. Exported as the
 * single source of truth: the compatibility app's search also gates on it
 * (don't redefine the contract value in two places).
 */
export const MIN_TERM_LEN = 2

export interface MedicationLookup {
  term: string
  data: MedicationSearchResponse | undefined
  isLoading: boolean
  isError: boolean
}

/**
 * Returns one lookup per input term, in the same order. Terms shorter than the
 * minimum are not queried (their lookup reports neither loading nor error).
 */
export function useMedicationLookups(terms: string[]): MedicationLookup[] {
  const results = useQueries({
    queries: terms.map((term) => ({
      queryKey: ['medication-search', term.toLowerCase()],
      queryFn: () => searchMedication(term),
      enabled: term.trim().length >= MIN_TERM_LEN,
      // Drug records are static reference data — cache them in the same class
      // as users (config-driven via app.yaml frontend_cache). Cold-start
      // UNAVAILABLE is covered by the QueryClient's default retry, so no
      // gRPC-layer retry band-aid is needed (cold starts are by design).
      staleTime: QUERY_STALE_TIMES.users,
    })),
  })

  return terms.map((term, i) => {
    const r = results[i]
    return {
      term,
      data: r?.data,
      isLoading: r?.isLoading ?? false,
      isError: r?.isError ?? false,
    }
  })
}
